import { useCallback, useRef, useMemo, memo } from 'react'

import {
  CameraCardFolder,
  useUserCameraCards,
  CameraCardItem,
  CameraCardFolderModal,
  CameraCardFolderMask,
} from '@shared'

import styles from './CardCollection.module.scss'
import { withCardCollectionModal, useCardCollectionModal } from './context'
import { ScrollProvider } from './context/ScrollContext'
import { useElementScrollPosition, useSmoothValue } from './hooks'

export const CardCollection = withCardCollectionModal(() => {
  const cards = useUserCameraCards()
  const containerRef = useRef<HTMLDivElement>(null)
  const { isOpen, onIsOpenChange, currentCard } = useCardCollectionModal()

  const renderMask = useCallback(
    (children: React.ReactNode) => {
      if (!currentCard) {
        return null
      }

      return (
        <CameraCardFolderMask maskType={currentCard.mask}>
          {children}
        </CameraCardFolderMask>
      )
    },
    [currentCard]
  )

  return (
    <ScrollProvider containerRef={containerRef}>
      <section className={styles.borderGradient}>
        <header className={styles.header}>
          <div className={styles.title}>Cards</div>
          <div className={styles.count}>
            <div className={styles.countCurrent}>{cards?.length || 0}</div>
            <div className={styles.countSeparator}>/</div>
            <div className={styles.countTotal}>100</div>
          </div>
        </header>
        <div ref={containerRef} className={styles.container}>
          {cards && cards.map((card, index) => (
            <ScrollCard
              key={index}
              card={card}
              containerRef={containerRef}
              index={index}
            />
          ))}
        </div>
      </section>
      {currentCard && (
        <CameraCardFolderModal
          isOpen={isOpen}
          onClose={() => onIsOpenChange(false)}
          {...currentCard}
          renderMask={renderMask}
        />
      )}
    </ScrollProvider>
  )
})

const ScrollCard = memo(
  ({
    card,
    containerRef,
    index,
  }: {
    card: CameraCardItem
    containerRef: React.RefObject<HTMLDivElement | null>
    index: number
  }) => {
    const ref = useRef<HTMLDivElement>(null)
    const { onIsOpenChange, onCurrentCardChange } = useCardCollectionModal()

    const { top, visible } = useElementScrollPosition({
      elementRef: ref,
      containerRef,
      offsetRem: -100,
    })

    const smoothTop = useSmoothValue({
      target: top,
      factor: 0.09,
      skip: !visible,
    })

    const height = useMemo(
      () => Number(ref.current?.clientHeight),
      [ref?.current?.clientHeight]
    )

    const topOffset = useMemo(() => {
      if (!height) return 0
      return height / 30
    }, [height])

    const style = useMemo(() => {
      const rotateX = Math.min(80, Math.max(0, smoothTop * 0.3))
      const translateY = Math.min(100, smoothTop * 0.5)
      const baseOffset = 70
      const translateX = -50

      return {
        transform: `
          translateX(${translateX}%)
          perspective(${600}rem)
          rotateX(-${rotateX}deg)
          translateY(-${translateY}rem)
          scale(0.8)
        `,
        top: `${index * baseOffset}rem`,
        opacity: top > topOffset || top < -height * 10 ? 1 : 0,
        transition: 'opacity 0.1s ease-in',
        filter: `drop-shadow(0px 0px 16px rgba(128, 128, 128, 0.15))`,
      }
    }, [smoothTop, index, visible, top, height, topOffset])

    const handleCardClick = useCallback(() => {
      onIsOpenChange(true)
      onCurrentCardChange(card)
    }, [onIsOpenChange, onCurrentCardChange, card])

    const renderMask = useCallback(
      (children: React.ReactNode) => {
        return (
          <CameraCardFolderMask maskType={card.mask}>
            {children}
          </CameraCardFolderMask>
        )
      },
      [card.mask]
    )

    return (
      <div
        ref={ref}
        className={styles.cardWrapper}
        style={style}
        onClick={handleCardClick}
      >
        <CameraCardFolder {...card} renderMask={renderMask} />
      </div>
    )
  }
)

ScrollCard.displayName = 'ScrollCard'
